<script lang="ts">
  import type {HelpInformation} from '@/types/settings';
  import {fly, fade} from 'svelte/transition';
  import {CollapsibleCard} from 'svelte-collapsible';
  import {createEventDispatcher} from 'svelte';
  import YouTube from 'svelte-youtube-embed';
  import {helpStore} from '@/store';

  const dispatch = createEventDispatcher();

  export let content: HelpInformation | undefined;
  export let width = 500;
  let htuOpen = !content?.bestPractices.length;
  let bpOpen = false;

  const getYouTubeVideoId = (url: string) => {
    const regex = /[?&]v=([^&#]*)/;
    const match = url.match(regex);
    return match && match[1];
  };
</script>

<aside class="ss-aside absolute inset-y-0 right-0 z-10 flex w-1/4 overflow-hidden xl:w-1/4" style="z-index:200;">
  <div
    bind:clientWidth={width}
    transition:fly={{x: width}}
    class="flex flex-col overflow-y-scroll bg-gray-100 shadow-xl"
  >
    <div class="flex items-center justify-between p-4">
      <p
        class="mx-2 flex items-center overflow-hidden text-ellipsis text-3xl font-medium uppercase text-gray-700 md:text-lg"
      >
        {content?.title}
      </p>
      <div class="flex items-center rounded-full">
        <i
          class="fa-solid fa-circle-xmark text-2xl text-primary"
          on:click={() => (dispatch('toggle-aside', {divId: null}), helpStore.set({activeId: null}))}
          on:keydown={() => (dispatch('toggle-aside', {divId: null}), helpStore.set({activeId: null}))}
        ></i>
      </div>
    </div>
    <div class="p-5">
      <span class="text-base font-normal text-gray-700 sm:text-sm">{content?.description}</span>
    </div>
    <div class="cards p-5">
      <CollapsibleCard open={!content?.bestPractices.length}>
        <h2
          slot="header"
          on:click={() => (htuOpen = !htuOpen)}
          on:keypress={() => (htuOpen = !htuOpen)}
          class="flex items-center justify-between text-3xl font-bold md:text-lg {htuOpen
            ? 'text-primary'
            : 'text-gray-700'}"
        >
          <p>How To Use</p>
          <div class="handle">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              class="h-10 w-10 md:h-6 md:w-6"
            >
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
            </svg>
          </div>
        </h2>
        <div slot="body" class="body border-b-2 border-gray-300 text-gray-700">
          <div class="text-justify text-base sm:text-sm">
            {@html content?.howTo}
          </div>
        </div>
      </CollapsibleCard>
      {#if content?.bestPractices.length}
        <CollapsibleCard open={false}>
          <h2
            slot="header"
            on:click={() => (bpOpen = !bpOpen)}
            on:keypress={() => (bpOpen = !bpOpen)}
            class="flex items-center justify-between text-3xl font-bold text-gray-700 md:text-lg {bpOpen
              ? 'text-primary'
              : 'text-gray-700'}"
          >
            <p>Best Practices</p>
            <div class="handle">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                class="h-10 w-10 md:h-6 md:w-6"
              >
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
              </svg>
            </div>
          </h2>
          <div slot="body" class="body border-b-2 border-gray-300 text-gray-700">
            <div class="text-justify text-base sm:text-sm">
              {@html content.bestPractices}
            </div>
          </div>
        </CollapsibleCard>
      {/if}
    </div>
    {#if content?.resourceUrl.length}
      <div class="ss-video-container flex w-full flex-col items-center justify-center object-cover p-4">
        <div>
          <h2 class="mb-4 flex items-center justify-between text-3xl font-bold text-primary md:text-lg">
            <p>Watch how it works</p>
          </h2>
        </div>
        <YouTube id={getYouTubeVideoId(content?.resourceUrl)} />
      </div>
    {/if}
  </div>
</aside>

<style lang="postcss" global>
  .cards {
    @apply w-full;
    width: 100%;
    margin: 0 auto;
  }

  :global(.card) {
    margin-top: 1em;
    overflow: hidden;
    font-family: 'Roboto', sans-serif;
  }

  :global(.card.open) .handle {
    transform: rotate(45deg);
    transform-origin: 50% 50%;
  }

  :global(.card-header) {
    @apply w-full;
  }

  .body {
    padding: 0.5em;
    margin-top: 0.25em;
    display: flex;
  }

  .body p {
    display: block;
    padding: 0;
    margin: 0;
  }

  .body p + p {
    margin-top: 1em;
  }

  .body a {
    @apply text-primary underline;
  }

  .ss-video-container > div {
    @apply w-full object-cover;
  }

  video {
    max-width: 100%;
    height: auto;
  }
</style>
